/**
 * Loudness envelope of an audio file, computed entirely in the browser — the
 * pauses that subtitle segmentation (lib/subtitleSegment.ts) cuts on.
 *
 * The file is already in hand (ResultPanel plays it locally), so nothing is
 * uploaded and nothing is fetched. Like audioSample.ts, every failure path
 * returns null instead of throwing: a missing envelope only makes the cut
 * coarser, it must never keep a finished transcription off the screen.
 */
import { HOP, type LoudnessEnvelope } from './subtitleSegment'
import { rmsFrames, type RmsJob } from './rmsFrames'

/** 8 kHz is plenty for an envelope and halves the memory of a long file. */
const TARGET_SAMPLE_RATE = 8000
/** 30 minutes ≈ 57 MB of Float32 at 8 kHz. Past that, segmentation falls back
 * to proportional timings rather than risking the tab. */
const MAX_SECONDS = 30 * 60

/** The envelope of a whole file, for subtitle segmentation. Refuses anything
 * over MAX_SECONDS — see the constant. */
export async function loudnessEnvelope(file: Blob): Promise<LoudnessEnvelope | null> {
  return envelopeOf(file, MAX_SECONDS)
}

/** The same measurement over a short excerpt, with its own ceiling.
 *
 * Chunking hands this a two-minute window cut out by ffmpeg, and a window
 * that small has nothing to do with MAX_SECONDS — that limit exists because
 * an hour of 8 kHz Float32 is 115 MB of memory, not because the arithmetic
 * stops working. Splitting the guard from the measurement is what lets the
 * chunker reuse this instead of copying it. */
export async function envelopeOf(file: Blob, maxSeconds: number): Promise<LoudnessEnvelope | null> {
  if (typeof OfflineAudioContext === 'undefined') return null
  try {
    // Decoding through an 8 kHz context resamples on the way in — decoding at
    // the device rate first would cost six times the memory for nothing
    const ctx = new OfflineAudioContext(1, 1, TARGET_SAMPLE_RATE)
    const decoded = await ctx.decodeAudioData(await file.arrayBuffer())
    if (!decoded || decoded.duration <= 0 || decoded.duration > maxSeconds) return null

    const samples = monoSamples(decoded)
    const rms = await framesInWorker(samples, decoded.sampleRate).catch(() =>
      rmsFrames(monoSamples(decoded), decoded.sampleRate, HOP),
    )
    return rms.length > 0 ? { hop: HOP, rms } : null
  } catch {
    return null
  }
}

/** A buffer of our own to transfer — the view from getChannelData() belongs to
 * the AudioBuffer. Stereo is averaged down, the way WebAudio would. */
function monoSamples(buffer: AudioBuffer): Float32Array {
  const out = new Float32Array(buffer.length)
  buffer.copyFromChannel(out, 0)
  if (buffer.numberOfChannels > 1) {
    const right = new Float32Array(buffer.length)
    buffer.copyFromChannel(right, 1)
    for (let i = 0; i < out.length; i++) out[i] = (out[i] + right[i]) / 2
  }
  return out
}

function framesInWorker(samples: Float32Array, sampleRate: number): Promise<Float32Array> {
  return new Promise((resolve, reject) => {
    if (typeof Worker === 'undefined') {
      reject(new Error('Web Workers unavailable'))
      return
    }
    const worker = new Worker(new URL('./rmsFrames.worker.ts', import.meta.url), { type: 'module' })
    worker.onmessage = (e: MessageEvent<Float32Array>) => {
      worker.terminate()
      resolve(e.data)
    }
    worker.onerror = (e) => {
      worker.terminate()
      reject(e.error ?? new Error('RMS worker failed'))
    }
    const job: RmsJob = { samples, sampleRate, hop: HOP }
    worker.postMessage(job, [samples.buffer])
  })
}
